import { ChangeDetectorRef, Component, Inject, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiResponse, ApiServiceInterface, FormServiceInterface } from '@hutsix/ngxh6';
import { environment } from '../../../../../../environments/environment';
import { PermitViewGrantService } from '../../../services/permit-view-grant.service';

interface PermitResult {
    /** TLC's per-type 'what happens next' line, shown on the confirmation screen. */
    submitMessage?: string | null;
    permitNumber: string;
    status: string;
    /** Shown on the confirmation so the applicant can see where it went. */
    email?: string | null;
    partySize?: number;
    token: string;
    viewGrant?: string;
    viewGrantExpiresAt?: number;
}

interface LookupResult {
    found: boolean;
    permitNumber?: string;
    permitType?: string;
    type?: string; // discriminator: 'fishing' | 'hunting' | 'work' | …
    status?: string;
    /** Humanised status, e.g. "In Review". */
    statusLabel?: string;
    active?: boolean;
    clanAreas?: string[];
}

/**
 * Fishing & Hunting share one component; the permit type (and thus the form slug)
 * comes from route data. The form is defined server-side in App\Form\Permit\FishingHuntingType
 * (two #[Form] slugs: fishing / hunting) and rendered by <ngxh6-form-embed>.
 */
@Component({
    selector: 'app-apply-fishing-hunting',
    templateUrl: './apply-fishing-hunting.component.html',
    styleUrls: ['./apply-fishing-hunting.component.scss'],
    standalone: false,
})
export class ApplyFishingHuntingComponent implements OnInit {
    public type: 'fishing' | 'hunting' = 'fishing';

    /**
     * Which route the applicant came in on, from the permit finder.
     *
     * A hint for the wording only. The permit number they enter is still what decides
     * the term, because that is resolved against the real permit server-side - someone
     * who picks "visiting" and then enters their own work permit gets an annual permit,
     * which is right. Undefined when the form is opened directly, and then the page
     * explains both routes as it always did.
     */
    public holder?: 'host' | 'self';
    public result?: PermitResult | null;

    // Host-permit lookup: a short-term visitor quotes their host's annual permit
    // number to see the clan areas they'll be able to fish/hunt in.
    public hostNumber = '';
    /** The address the quoted permit was issued to - the host's, or your own on an annual. */
    public hostEmail = '';
    public lookingUp = false;
    public lookupResult?: LookupResult | null;
    public lookupError?: string;
    // Pre-fill + (possibly area-restricted) form options once a valid permit resolves.
    public formModel?: { referencedPermitNumber: string; referencedPermitEmail: string; term: string } | null;
    public formOpts?: any;

    /** The form only opens once a usable permit is resolved (a host fishing/hunting
     *  permit with areas, or an active work permit for eligibility). */
    public get canProceed(): boolean {
        const r = this.lookupResult;
        if (!r?.found) return false;
        if (r.type === 'fishing' || r.type === 'hunting') return !!r.clanAreas?.length;
        if (r.type === 'work') return !!r.active;
        return false;
    }

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        @Inject('FormService') private formService: FormServiceInterface,
        private route: ActivatedRoute,
        private grants: PermitViewGrantService,
        private zone: NgZone,
        private cdRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.type = this.route.snapshot.data['type'] === 'hunting' ? 'hunting' : 'fishing';

        const holder = this.route.snapshot.queryParamMap.get('holder');
        this.holder = 'host' === holder || 'self' === holder ? holder : undefined;
    }

    /** Restates what the finder promised, so the form does not drop that context. */
    public get outcomeLine(): string {
        return this.type === 'hunting'
            ? 'Assessed by TLC with clan consultation, and you will need a copy of your current shooter’s licence.'
            : 'Assessed by TLC with clan consultation. A permit covers Red Zone waters, by boat - fishing from land needs no permit.';
    }

    /**
     * "14-day Fishing Permit" once we know which route they are on, so the page names
     * the permit they are actually applying for rather than the family it belongs to.
     */
    public get title(): string {
        const base = this.type === 'hunting' ? 'Hunting Permit' : 'Fishing Permit';

        if ('host' === this.holder) {
            return '14-day '.concat(base);
        }
        if ('self' === this.holder) {
            return 'Annual '.concat(base);
        }

        return base;
    }

    /**
     * The crumb goes back to the eligibility CHOICE question for this type (the three
     * buttons: visiting under a host, resident, or neither) - not the guest/annual
     * outcome leaf this form was reached through. That question is where the applicant
     * picked their route, so it is the one to let them change it. `this.type` is always
     * set from the route, so `fishing-eligibility` / `hunting-eligibility` is always valid.
     */
    /** Back to this type's info page (fishing / hunting), where the 14-day vs annual choice is. */
    public get previousRoute(): string {
        return `/${this.type}`;
    }

    public get previousLabel(): string {
        // Names the permit (e.g. "Fishing Permit" / "14-day Fishing Permit"), matching the
        // other types' crumbs; the target is this type's eligibility question.
        return this.title;
    }

    /**
     * The heading over the lookup, which cannot be one sentence for everybody.
     *
     * "Already hold a permit?" is wrong for a short-term visitor - they hold nothing,
     * which is the whole reason they are quoting somebody else's permit. Asking them
     * that invites a "no" and a dead end on the first thing they read.
     */
    public get lookupHeading(): string {
        if ('host' === this.holder) {
            return 'Your host\u2019s permit';
        }
        if ('self' === this.holder) {
            return 'Your work permit';
        }

        return 'Which permit are you applying under?';
    }

    /**
     * The nudge under the empty form. Same trap as the heading: "your permit" is not
     * the permit a short-term visitor is looking up.
     */
    public get lookupNudge(): string {
        if ('host' === this.holder) {
            return 'Look up your host\u2019s permit above to start your application.';
        }
        if ('self' === this.holder) {
            return 'Look up your work permit above to start your application.';
        }

        return 'Look up a permit above to start your application.';
    }

    /** What to ask for, given how they got here. */
    public get lookupPrompt(): string {
        if ('host' === this.holder) {
            return `Enter your host\u2019s annual ${this.type} permit number and the email address it was issued to. Your permit covers the ${this.areaWordPlural} theirs is approved for.`;
        }
        if ('self' === this.holder) {
            return 'Enter your work or long-term resident permit number and the email address it was issued to, so we can check you are eligible.';
        }

        return '';
    }

    public get lookupPlaceholder(): string {
        if ('host' === this.holder) {
            return this.type === 'hunting' ? 'e.g. HP00042' : 'e.g. FP00042';
        }
        if ('self' === this.holder) {
            return 'e.g. WP00042';
        }

        return 'e.g. FP00042 or WP00042';
    }

    /** Whose address to ask for - the host's on a 14-day, your own on an annual. */
    public get lookupEmailPlaceholder(): string {
        if ('host' === this.holder) {
            return "Host's email address";
        }
        if ('self' === this.holder) {
            return 'Your email address';
        }

        return 'Email the permit was issued to';
    }

    /**
     * What a piece of country is called on this form.
     *
     * Fishing moved to river-system zones so clan boundaries stay off the water;
     * hunting still selects clan areas. Same field underneath, different word.
     */
    public get areaWord(): string {
        return this.type === 'fishing' ? 'zone' : 'hunting area';
    }

    public get areaWordPlural(): string {
        return this.type === 'fishing' ? 'zones' : 'hunting areas';
    }

    public get verb(): string {
        return this.type === 'hunting' ? 'hunt' : 'fish';
    }

    public lookupHost(): void {
        const num = this.hostNumber.trim();
        const email = this.hostEmail.trim();
        if (!num || !email || this.lookingUp) {
            return;
        }
        this.lookingUp = true;
        this.lookupError = undefined;
        this.lookupResult = null;
        this.formModel = null;
        this.formOpts = null;
        this.api
            .get({
                // Both, or nothing comes back: a number on its own now returns
                // found:false, exactly as an unknown one does.
                url: `/api/public/permit/lookup/${encodeURIComponent(num)}?email=${encodeURIComponent(email)}`,
                skipAuth: true,
                bypassOfflineQueue: true,
                useCache: false,
                displayErrors: false,
            })
            .subscribe(
                // The guest GET (bypassOfflineQueue) resolves nested in the zone but off
                // zone.js's task tracking, so no change-detection runs when it lands: the
                // state updates but the view stays stuck on "Looking up…". detectChanges()
                // forces this component's view to re-render; zone.run keeps the
                // ngxh6-form-embed that formOpts triggers inside the zone. (Guest-GET note
                // in memory.)
                (res: ApiResponse<LookupResult>) =>
                    this.zone.run(() => {
                        this.lookingUp = false;
                        this.lookupResult = res.data;
                        if (this.canProceed && res.data) {
                            // Carry the resolved number + the term it implies into the form.
                            this.formModel = {
                                referencedPermitNumber: res.data.permitNumber ?? '',
                                // Carried into the form so the submit can re-check the pair -
                                // the number is a hidden field, so a form posted directly
                                // would otherwise skip the gate this lookup applies.
                                referencedPermitEmail: email,
                                term: res.data.type === 'work' ? 'annual' : '14_day',
                            };
                            // Fetch the form shaped to the referenced permit (?ref): a host
                            // fishing/hunting permit limits the clan areas + term to 14-day;
                            // a work permit limits the term to annual (all areas).
                            const base: any = this.formService.getForm(this.type as any);
                            this.formOpts = { ...base, api_get: `${base.api_get}?ref=${encodeURIComponent(res.data.permitNumber ?? '')}` };
                        } else {
                            this.formModel = null;
                            this.formOpts = null;
                        }
                        this.cdRef.detectChanges();
                    }),
                () =>
                    this.zone.run(() => {
                        this.lookingUp = false;
                        this.lookupError = 'Could not look that up. Please try again.';
                        this.cdRef.detectChanges();
                    }),
            );
    }

    public get pdfUrl(): string | null {
        return this.result ? `${environment.apiUrl}/api/public/permit/${this.result.token}/pdf` : null;
    }

    public onSubmitted(res: ApiResponse<PermitResult>): void {
        if (res?.data?.status) {
            this.result = res.data;
            // They just gave us this email on the form - don't re-ask on the next click.
            this.grants.remember(res.data);
        }
    }
}
